import { isAnonymousChatAtom } from '@/atoms/chatLaunchAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useAtomValue } from 'jotai';
import { Sparkles, X } from 'lucide-react';
import { useRouter } from 'next/router';
import Markdown from 'react-markdown';

interface ChatCompletionToastProps {
  chatId: string;
  title: string;
  description: string;
  onDismiss?: () => void;
}

export const ChatCompletionToast = ({
  chatId,
  title,
  description,
  onDismiss,
}: ChatCompletionToastProps) => {
  const router = useRouter();
  const { trackToastViewClicked } = useEvidenceAnalytics();

  const isAnonymousChat = useAtomValue(isAnonymousChatAtom);

  const handleView = () => {
    trackToastViewClicked(chatId);
    onDismiss?.();
    if (isAnonymousChat) {
      void router.push(`/chat/${chatId}`);
    } else {
      void router.push(`/scribe/chat/${chatId}`);
    }
  };

  return (
    <div
      className={cn(
        'flex w-96 flex-row items-center gap-3 rounded-lg border border-outline bg-surface p-4 shadow-lg'
      )}
    >
      <Sparkles size={20} className="shrink-0 text-text-primary" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyP2 className="text-text-primary">{title}</TypographyP2>
        <div className="line-clamp-2 text-xs text-text-secondary [&>p]:m-0 [&_strong]:font-medium">
          <Markdown>{description}</Markdown>
        </div>
      </div>
      <Button onClick={handleView} variant="default" size="sm" className="shrink-0">
        View evidence
      </Button>
      <button
        onClick={onDismiss}
        className="shrink-0 rounded p-1 text-text-secondary transition-colors duration-150 ease-out hover:bg-surface-hover hover:text-text-primary"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};
